import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Autocomplete, Box, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { useMemo } from "react";
import useColors from "../../../../../api/hooks/useColors";
import AnchorPopper from "../../../../../components/forms/AnchorPopper";
import {
  getTextFieldInputProps,
  StyledTextField,
  textFieldLabelProps,
} from "../../../../../components/forms/styles/formFieldStyles";

const AsanaProjects = ({ options, showOptions, required = false }) => {
  const { values, errors, touched, handleBlur, setFieldValue } =
    useFormikContext();
  const colors = useColors();

  const hasError = !!touched.projects && !!errors.projects;
  const errorMessage =
    hasError && typeof errors.projects === "string" ? errors.projects : "";

  const projectsLabel = useMemo(() => {
    const labelText =
      values.projects.length > 0 ? "Projects" : "Select projects";

    return <Typography variant="caption">{labelText}</Typography>;
  }, [values.projects]);

  const getColorValue = (asanaColor) => {
    if (!asanaColor) return "#dddddd";

    // Construct the theme color key from the Asana color name
    const colorKey = `asana_${asanaColor.replace("-", "_")}`;
    return colors[colorKey] || "#dddddd";
  };

  const ColorDot = ({ color }) => (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: "8px",
        height: "8px",
        borderRadius: "50%",
        backgroundColor: getColorValue(color),
        marginRight: "8px",
      }}
    />
  );

  return (
    <Autocomplete
      options={options || []}
      disabled={!showOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) =>
        option.gid === (value.gid || value)
      }
      value={values.projects || []}
      multiple
      filterSelectedOptions
      onChange={(_, newValue) => setFieldValue("projects", newValue)}
      popupIcon={<ExpandMoreIcon />}
      PopperComponent={AnchorPopper}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((option, index) => {
          const { onDelete, ...tagProps } = getTagProps({
            index,
          });

          return (
            <Box
              {...tagProps}
              key={index}
              component="div"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: colors.grey_200,
                borderRadius: "16px",
                padding: "3px 8px 3px 5px",
                margin: "3px",
              }}
            >
              <ColorDot color={option?.color} />
              <Typography variant="caption" sx={{ lineHeight: 1 }}>
                {option.name}
              </Typography>
              {onDelete && (
                <Box
                  component="span"
                  onClick={onDelete}
                  sx={{
                    cursor: "pointer",
                    ml: 0.5,
                    fontSize: "16px",
                    lineHeight: 1,
                    color: colors.grey_500,
                    "&:hover": {
                      color: colors.grey_700,
                    },
                  }}
                >
                  ×
                </Box>
              )}
            </Box>
          );
        })
      }
      renderOption={(props, option) => {
        return (
          <Box
            component="li"
            sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            {...props}
          >
            <ColorDot color={option?.color} />
            <Typography variant="caption">{option.name}</Typography>
          </Box>
        );
      }}
      renderInput={(params) => (
        <StyledTextField
          {...params}
          margin="dense"
          label={projectsLabel}
          fullWidth
          required={required}
          error={hasError}
          helperText={errorMessage}
          onBlur={handleBlur}
          InputProps={getTextFieldInputProps(params.InputProps)}
          InputLabelProps={textFieldLabelProps(params.InputLabelProps)}
        />
      )}
    />
  );
};
export default AsanaProjects;
